import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Stack, Typography } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import type { SubscriptionTier } from '@/types/dtos/transactions/subscription-plan.dto';
import {
  getManagePlanFeatureListBullets,
  resolveIncludedFeatureKeyDisplayRows,
  resolveIncludedQuotaDisplayRows,
} from '@/helpers/manage-plan-feature-display';

/**
 * Manage-plan card “Includes” body:
 * 1. Marketing bullets from `managePlanFeatureLists.<productKey>.<tier>` (when present)
 * 2. Included quotas / commercial feature keys newly added relative to the previous tier
 */
export default function ManagePlanIncludesList({
  productKey,
  tier,
  includedQuotas,
  featureKeys,
  previousIncludedQuotas,
  previousFeatureKeys,
}: {
  productKey: string;
  tier: SubscriptionTier | string;
  includedQuotas?: { quotaKey: string; quantity: number }[];
  featureKeys?: string[];
  previousIncludedQuotas?: { quotaKey: string; quantity: number }[];
  previousFeatureKeys?: string[];
}) {
  const t = useTranslations();
  const marketingBullets = getManagePlanFeatureListBullets(t, productKey, tier);
  const quotaRows = resolveIncludedQuotaDisplayRows(t, includedQuotas, previousIncludedQuotas);
  const featureRows = resolveIncludedFeatureKeyDisplayRows(t, featureKeys, previousFeatureKeys);
  const hasIncluded = quotaRows.length > 0 || featureRows.length > 0;

  if (marketingBullets.length === 0 && !hasIncluded) {
    return null;
  }

  return (
    <Stack spacing={0.75} sx={{ flex: 1 }}>
      {marketingBullets.map((line, idx) => (
        <Stack key={`i18n-feature-${tier}-${idx}`} direction="row" spacing={1} alignItems="flex-start">
          <CheckCircleOutlineIcon color="primary" sx={{ fontSize: 18, flexShrink: 0, mt: 0.125 }} />
          <Typography variant="body2" color="text.secondary">
            {line}
          </Typography>
        </Stack>
      ))}
      {quotaRows.map((row) => (
        <Stack key={`quota-${row.quotaKey}`} direction="row" spacing={1} alignItems="flex-start">
          <CheckCircleOutlineIcon color="primary" sx={{ fontSize: 18, flexShrink: 0, mt: 0.125 }} />
          <Typography variant="body2" color="text.secondary">
            {row.line}
          </Typography>
        </Stack>
      ))}
      {featureRows.map((row) => (
        <Stack key={`feature-${row.featureKey}`} direction="row" spacing={1} alignItems="flex-start">
          <CheckCircleOutlineIcon color="primary" sx={{ fontSize: 18, flexShrink: 0, mt: 0.125 }} />
          <Typography variant="body2" color="text.secondary">
            {row.line}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}
